import { useEffect, useRef, useState, useCallback } from 'react';

interface ImageItem {
  url: string;
  alt: string;
  caption: string;
}

interface Section {
  id: number;
  year: string;
  eyebrow: string;
  title: string;
  paragraphs: string[];
  imageLeft: boolean;
  images: ImageItem[];
}

const sections: Section[] = [
  {
    id: 1,
    year: '1870',
    eyebrow: 'Origins in Jhelum and Gujrat',
    title: 'The History of M. Hayat & Co.',
    paragraphs: [
      'The history of M. Hayat & Co. began in 1870, when Mohammad Hayat established the business in Jhelum. After an initial period of growth, the workshop was moved to a larger setup in Gujrat. As the family sought to expand their craft across undivided India, Mohammad Hayat and his three brothers decided to establish their presence in different regions.',
      'One of the brothers, Mian Ghulam Rasool, settled in Rawalpindi, founding the workshop that would eventually become the modern M. Hayat & Co.',
    ],
    imageLeft: true,
    images: [
      {
        url: 'https://static.readdy.ai/image/a84fce21c15147349888dd3eeb9b8000/4a35517d3579209c54ceb39cf0ce8cb3.png',
        alt: 'Mian Ghulam Rasool with his grandsons Salman (left) and Sohail (right)',
        caption: 'Mian Ghulam Rasool with his grandsons Salman (left) and Sohail (right)',
      },
    ],
  },
  {
    id: 2,
    year: '1940s',
    eyebrow: 'M. Ashraf and the Move to Bank Road',
    title: 'A New Chapter of Expansion',
    paragraphs: [
      'In the 1940s, the business entered a new chapter when Ghulam Rasool brought in Mohammad Ashraf into the business. Over the years, M. Ashraf became an essential part of both the family and the business, bringing in new ideas, designs, and establishing a new clientele. As Ghulam Rasool grew older, he entrusted M. Ashraf with increasing responsibility, eventually handing over the leadership of the firm.',
      'Under M. Ashraf\'s management, the business expanded significantly, leading to the establishment of the factory and showroom at 60 Edwardes Road — known today as Bank Road — in the heart of Rawalpindi.',
    ],
    imageLeft: false,
    images: [
      {
        url: 'https://static.readdy.ai/image/a84fce21c15147349888dd3eeb9b8000/3730f7210afe1404c07464a1a98f491d.jpeg',
        alt: 'M. Ashraf (far left) took the business to new heights',
        caption: 'M. Ashraf, the third generation proprietor of the business',
      },
    ],
  },
  {
    id: 3,
    year: '1950',
    eyebrow: 'Official Cabinet Makers to the Military',
    title: 'A Reputation Built on Trust',
    paragraphs: [
      'As a garrison town for both the British and Pakistani armies, Rawalpindi was home to a constant rotation of officers. M. Hayat & Co. supported this community by offering furniture on rent for the duration of their postings, a practice that built a lasting rapport with the military.',
      'This reputation for reliability and quality eventually led to the firm being appointed as the "Official Cabinet Makers" for General Douglas Gracey, Commander-in-Chief of the Pakistan Army in 1950.',
    ],
    imageLeft: true,
    images: [
      {
        url: 'https://static.readdy.ai/image/a84fce21c15147349888dd3eeb9b8000/6613af3c24d31e49ef4661b525a35a7e.jpeg',
        alt: 'Letter of correspondence between Sir Douglas Gracey and M. Ashraf',
        caption: 'A letter of correspondence between Sir Douglas Gracey and M. Ashraf.',
      },
      {
        url: 'https://static.readdy.ai/image/a84fce21c15147349888dd3eeb9b8000/ce59165de5890a56528f0c4325a216a3.jpeg',
        alt: 'Official Cabinet Makers to Sir Douglas Gracey',
        caption: 'Official Cabinet Makers to Sir Douglas Gracey.',
      },
    ],
  },
  {
    id: 4,
    year: '1981',
    eyebrow: 'The Development of Islamabad & Presidential Recognition',
    title: "Serving the Nation's Capital",
    paragraphs: [
      'The 1960s brought another pivotal shift as the construction of Islamabad drew government officials and foreign dignitaries to the Twin Cities. M. Hayat & Co. became the choice for many embassy residences and offices, providing furniture that met the standards of the growing diplomatic community.',
      'This era of service culminated in the 1980s, when the firm was designated as the "Official Cabinet Makers" for the President of Pakistan in 1981.',
    ],
    imageLeft: false,
    images: [
      {
        url: 'https://static.readdy.ai/image/a84fce21c15147349888dd3eeb9b8000/a2e678ddc8cf849f850e4cee76ab1f19.jpeg',
        alt: 'Official Cabinet Makers to the President of Pakistan',
        caption: 'Official Cabinet Makers to the President of Pakistan.',
      },
    ],
  },
  {
    id: 5,
    year: 'Present',
    eyebrow: 'Carrying the Legacy Forward',
    title: 'A Living Heritage',
    paragraphs: [
      'In the 1990s, the business was inherited by M. Ashraf\'s sons, Sohail and Salman, who continued the family\'s work on Bank Road. After decades in the Saddar area, the business transitioned to its current form in the 2000s.',
      'Today, Salman Ashraf and his wife, Sobia Salman, manage M. Hayat & Co., bringing new ideas to the classic Victorian and French designs that have defined the family\'s craftsmanship for over 150 years.',
    ],
    imageLeft: true,
    images: [
      {
        url: 'https://static.readdy.ai/image/a84fce21c15147349888dd3eeb9b8000/0eecab76add33b90f66dd5d526a1573e.jpeg',
        alt: 'Salman Ashraf and Sobia Salman, current proprietors at M. Hayat & Co.',
        caption: 'Salman Ashraf and Sobia Salman, current proprietors at M. Hayat & Co.',
      },
    ],
  },
];

function useScrollReveal(threshold = 0.12) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, visible };
}

interface TimelineSectionProps {
  section: Section;
  index: number;
}

function ImageBlock({ section, visible }: { section: Section; visible: boolean; imageLeft: boolean }) {
  const anim = section.imageLeft
    ? `transition-all duration-[1000ms] ease-out ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`
    : `transition-all duration-[1000ms] ease-out ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`;

  const isSingleVertical = section.id === 4;
  const isDoubleStack = section.images.length > 1;

  return (
    <div className={`flex flex-col gap-5 ${anim}`}>
      {section.images.map((img, i) => (
        <figure key={i} className="relative group">
          <div className="relative overflow-hidden rounded-sm shadow-sm">
            {/* Double frame */}
            <div className="absolute inset-0 border border-brand-gold/25 rounded-sm z-10 pointer-events-none" />
            <div className="absolute inset-[7px] border border-brand-gold/12 rounded-sm z-10 pointer-events-none" />
            <div
              className="w-full overflow-hidden"
              style={{
                height: isDoubleStack
                  ? '320px'
                  : isSingleVertical
                  ? '560px'
                  : '460px',
              }}
            >
              <img
                src={img.url}
                alt={img.alt}
                className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-[1.03]"
              />
            </div>
            <div className="absolute inset-0 bg-brand-darkbrown/6 group-hover:bg-transparent transition-colors duration-500 pointer-events-none" />
          </div>
          <figcaption className="mt-3 font-display text-[10px] tracking-[2px] text-brand-muted/80 uppercase text-center">
            {img.caption}
          </figcaption>
        </figure>
      ))}
    </div>
  );
}

function TextBlock({ section, visible }: { section: Section; visible: boolean }) {
  const anim = section.imageLeft
    ? `transition-all duration-[1000ms] ease-out delay-150 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`
    : `transition-all duration-[1000ms] ease-out delay-150 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`;

  return (
    <div className={`flex flex-col justify-center ${anim}`}>
      {/* Eyebrow */}
      <div className="flex items-center gap-3 mb-5">
        <div className="h-px w-10 bg-brand-gold flex-shrink-0" />
        <span className="font-display text-brand-gold text-[10px] tracking-[4px] uppercase leading-none">
          {section.eyebrow}
        </span>
      </div>

      {/* Year + Title */}
      <div className="flex items-start gap-4 mb-7">
        <span className="font-serif text-[80px] leading-none font-bold text-brand-gold/12 select-none hidden lg:block flex-shrink-0 -mt-2">
          {section.year}
        </span>
        <div>
          <span className="font-display text-brand-gold text-xs tracking-[3px] uppercase block mb-2 lg:hidden">
            {section.year}
          </span>
          <h2 className="font-serif text-3xl md:text-4xl text-brand-darkbrown font-bold leading-snug">
            {section.title}
          </h2>
        </div>
      </div>

      {/* Paragraphs */}
      <div className="space-y-5">
        {section.paragraphs.map((p, i) => (
          <p key={i} className="font-sans text-[14.5px] text-brand-muted leading-[2] tracking-wide">
            {p}
          </p>
        ))}
      </div>

      {/* Bottom accent */}
      <div className="mt-10 flex items-center gap-3">
        <div className="h-px w-16 bg-brand-gold/35" />
        <div className="w-1.5 h-1.5 rounded-full bg-brand-gold/50 flex-shrink-0" />
        <div className="h-px flex-1 bg-gradient-to-r from-brand-gold/20 to-transparent" />
      </div>
    </div>
  );
}

function TimelineSection({ section, index }: TimelineSectionProps) {
  const { ref, visible } = useScrollReveal();
  const bg = index % 2 === 0 ? 'bg-brand-cream' : 'bg-brand-beige';

  return (
    <section className={`${bg} py-24 md:py-32 px-6 overflow-hidden`} ref={ref}>
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 xl:gap-24 items-center">
          {section.imageLeft ? (
            <>
              <ImageBlock section={section} visible={visible} imageLeft={section.imageLeft} />
              <TextBlock section={section} visible={visible} />
            </>
          ) : (
            <>
              <TextBlock section={section} visible={visible} />
              <ImageBlock section={section} visible={visible} imageLeft={section.imageLeft} />
            </>
          )}
        </div>
      </div>
    </section>
  );
}

function SectionDivider() {
  return (
    <div className="flex items-center justify-center py-3 bg-brand-cream">
      <div className="flex items-center gap-3">
        <div className="h-px w-20 bg-brand-gold/20" />
        <div className="w-1.5 h-1.5 rounded-full bg-brand-gold/45" />
        <div className="w-1 h-1 rounded-full bg-brand-gold/25" />
        <div className="w-1.5 h-1.5 rounded-full bg-brand-gold/45" />
        <div className="h-px w-20 bg-brand-gold/20" />
      </div>
    </div>
  );
}

export default function HeritageTimeline() {
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerVisible, setHeaderVisible] = useState(false);

  const observe = useCallback(() => {
    const el = headerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHeaderVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const cleanup = observe();
    return cleanup;
  }, [observe]);

  return (
    <>
      {/* Section Header */}
      <div
        ref={headerRef}
        className={`bg-brand-cream pt-20 pb-12 px-6 text-center transition-all duration-700 ${
          headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        <span className="font-display text-brand-gold text-[10px] tracking-[5px] uppercase block mb-4">
          A Museum of Memories
        </span>
        <h2 className="font-serif text-4xl md:text-5xl text-brand-darkbrown font-bold mb-5">
          Our <em>Heritage</em> Story
        </h2>
        <p className="font-sans text-brand-muted text-sm max-w-xl mx-auto leading-relaxed">
          Over 150 years of craftsmanship, trust, and legacy — told through the chapters that shaped M. Hayat &amp; Co.
        </p>
        <div className="flex items-center justify-center gap-3 mt-8">
          <div className="h-px w-16 bg-brand-gold/30" />
          <div className="w-2 h-2 rounded-full bg-brand-gold/60" />
          <div className="h-px w-16 bg-brand-gold/30" />
        </div>
      </div>

      {/* Timeline Sections */}
      {sections.map((section, index) => (
        <div key={section.id}>
          <TimelineSection section={section} index={index} />
          {index < sections.length - 1 && <SectionDivider />}
        </div>
      ))}
    </>
  );
}
